/**
 * GeneralTabFinancialsCard Component
 *
 * Renders the Financials card for the General tab, displaying:
 * - Financial metrics table (Sales, Gross profit, EBITDA, EBIT, EBT, Net income, Total assets, Cash)
 * - Year columns with formatted currency values and percentage indicators
 * - Loading and error states
 * - Mobile scroll indicator
 *
 * CALCULATION OWNERSHIP: This component receives all data and formatting from parent.
 * Currency formatting is done via the formatCurrency prop.
 */

import { Fragment } from 'react';
import PropTypes from 'prop-types';
import GeneralLoader from '../../../loaders/GeneralLoader';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import ProfilerCurrencyFormatSwitch from '../../../controls/ProfilerCurrencyFormatSwitch';
import { Tooltip } from 'react-tooltip';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { formatNumber } from '../../../../utils/utils';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { PROFILER_FINANCIAL_STATEMENTS } from '../../../../utils/constants/profilerFinancials';

/**
 * Performance indicator sub-label for percentage values
 */
const PerformanceIndicator = ({ percentage }) => {
  if (!percentage) {
    return <div className="sub-label">-</div>;
  }

  const percentageValue = Number.parseFloat(percentage);

  if (Number.isNaN(percentageValue)) {
    return <div className="sub-label">{percentageValue}</div>;
  }

  const subLabelClassNames = ['sub-label', 'performance-indicator'];
  if (percentageValue < 0) {
    subLabelClassNames.push('red');
  } else if (percentageValue <= 2) {
    subLabelClassNames.push('grey');
  } else {
    subLabelClassNames.push('green');
  }

  return <div className={subLabelClassNames.join(' ')}>{percentage}</div>;
};

PerformanceIndicator.propTypes = {
  percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

/**
 * @param {Object} props
 * @param {Object} props.financials - Financial data { years, revenues, grossProfit, ebitda, etc. }
 * @param {boolean} props.isLoading - Whether details are loading
 * @param {boolean} props.hasError - Whether there was an error loading details
 * @param {string} props.layout - 'desktop' | 'mobile' | 'tablet'
 * @param {boolean} props.isScrollable - Whether values section is scrollable (mobile)
 * @param {Object} props.labelStyle - Style object for label widths
 * @param {Function} props.formatCurrency - Currency formatting function (value, fallback) => string
 * @param {Object} props.valuesRef - Ref for the values scroll container
 * @param {boolean} props.canViewExtendedData - Whether user can view extended profiler data
 * @param {boolean} props.showEmptyRows - Render every metric row incl. empty ones (Full).
 *   When false (Lite / Demo-Lite), metric rows with no data are omitted rather than shown as hyphens.
 */
const GeneralTabFinancialsCard = ({
  financials = {},
  isLoading = false,
  hasError = false,
  layout = 'desktop',
  isScrollable = false,
  labelStyle = {},
  formatCurrency = (v, fb) => (v != null ? v : fb),
  valuesRef = null,
  canViewExtendedData = true,
  showEmptyRows = true
}) => {
  // Don't render if user can't view extended data
  if (!canViewExtendedData) {
    return null;
  }

  const years = financials?.years || [];
  const revenues = financials?.revenues || [];
  const grossProfit = financials?.grossProfit || [];
  const ebitda = financials?.ebitda || [];
  const ebit = financials?.[PROFILER_FINANCIAL_STATEMENTS.EBIT.name] || financials?.EBIT || [];
  const ebt = financials?.[PROFILER_FINANCIAL_STATEMENTS.EBT.name] || financials?.EBT || [];
  const netIncome = financials?.netIncome || [];
  const totalAssets = financials?.totalAssets || [];
  const cashAndEquivalents = financials?.cashAndEquivalents || [];
  const employees = financials?.employees || [];

  // Employees growth % is data-driven: only surface the "Growth in %" sub-label
  // (and its per-year indicators) once the payload actually carries a percentage
  // on the employees series. Until then the row stays headcount-only, so this is
  // safe to ship ahead of the BE field (TD-2616) — it lights up automatically.
  const employeesHaveGrowth = employees.some(
    (e) => e?.percentage != null && `${e.percentage}`.trim() !== ''
  );

  const getValueForYear = (dataArray, year) => {
    return dataArray.find((obj) => obj.year === year);
  };

  // Reusable row renderer for currency-style financial metrics
  const renderFinancialRow = (
    dataArray,
    keyPrefix,
    { isLast = false, showPercentage = true } = {}
  ) => (
    <div className={`labels-wrap${isLast ? ' last' : ''}`}>
      {years.map((year) => {
        const entry = getValueForYear(dataArray, year);
        return (
          <div className="labels" key={`${year}-${keyPrefix}`} style={labelStyle}>
            <div className="label">{formatCurrency(entry?.value, PUNCTUATION.HYPHEN)}</div>
            {showPercentage && <PerformanceIndicator percentage={entry?.percentage} />}
          </div>
        );
      })}
      {!years.length && (
        <div className="labels" style={labelStyle}>
          <div className="label">{PUNCTUATION.EMPTY_VALUE}</div>
          {showPercentage && <div className="sub-label">&nbsp;</div>}
        </div>
      )}
    </div>
  );

  // Revenue row — currency with growth indicator + self-disclosure tooltip
  const renderRevenueRow = (dataArray, { isLast = false } = {}) => (
    <div className={`labels-wrap${isLast ? ' last' : ''}`}>
      {years.map((year) => {
        const entry = getValueForYear(dataArray, year);
        const value = entry?.value;
        const isBasic = entry?.source === 'basic';
        const id = 'lw-revenues' + year;
        return (
          <div className="labels" key={id} style={labelStyle}>
            <div className="label" style={isBasic ? { color: '#348cf9' } : {}}>
              <font id={id}>{formatCurrency(value, PUNCTUATION.HYPHEN)}</font>
            </div>
            <PerformanceIndicator percentage={entry?.percentage} />
            {isBasic && (
              <Tooltip
                anchorSelect={`#${id}`}
                multiline={true}
                variant="dark"
                place="bottom-center"
                opacity={1}
                content={<>Based on self-disclosure or estimate</>}
              />
            )}
          </div>
        );
      })}
      {!years.length && (
        <div className="labels" style={labelStyle}>
          <div className="label">{PUNCTUATION.EMPTY_VALUE}</div>
          <div className="sub-label">&nbsp;</div>
        </div>
      )}
    </div>
  );

  // Employees row — headcount (not currency), self-disclosed years marked.
  // Growth % renders only when the series carries percentages (showPercentage),
  // mirroring the Sales row so the label/value columns stay aligned.
  const renderEmployeesRow = (dataArray, { isLast = false, showPercentage = false } = {}) => (
    <div className={`labels-wrap${isLast ? ' last' : ''}`}>
      {years.map((year) => {
        const entry = getValueForYear(dataArray, year);
        const hasValue = entry?.value != null && !Number.isNaN(Number(entry.value));
        const isBasic = entry?.source === 'basic';
        const id = 'lw-employees' + year;
        return (
          <div className="labels" key={id} style={labelStyle}>
            <div className="label" style={isBasic ? { color: '#348cf9' } : {}}>
              <font id={id}>{hasValue ? formatNumber(entry.value) : PUNCTUATION.HYPHEN}</font>
            </div>
            {showPercentage && <PerformanceIndicator percentage={entry?.percentage} />}
            {isBasic && (
              <Tooltip
                anchorSelect={`#${id}`}
                multiline={true}
                variant="dark"
                place="bottom-center"
                opacity={1}
                content={<>Based on self-disclosure or estimate</>}
              />
            )}
          </div>
        );
      })}
      {!years.length && (
        <div className="labels" style={labelStyle}>
          <div className="label">{PUNCTUATION.EMPTY_VALUE}</div>
          {showPercentage && <div className="sub-label">&nbsp;</div>}
        </div>
      )}
    </div>
  );

  // Ordered metric descriptors drive BOTH the label column and the value column,
  // so filtering empty rows (Lite / Demo-Lite → revenue + employees only, TD-2499)
  // keeps the two columns structurally aligned. showEmptyRows=true (Full) = all rows.
  const metricRows = [
    { key: 'revenues', label: 'Sales', subLabel: 'Growth in %', data: revenues, kind: 'revenue' },
    {
      key: 'gross-profit',
      label: 'Gross profit',
      subLabel: 'in % of sales',
      data: grossProfit,
      kind: 'currency'
    },
    { key: 'ebitda', label: 'EBITDA', subLabel: 'in % of sales', data: ebitda, kind: 'currency' },
    {
      key: 'ebit',
      label: PROFILER_FINANCIAL_STATEMENTS.EBIT.name,
      subLabel: 'in % of sales',
      data: ebit,
      kind: 'currency'
    },
    {
      key: 'ebt',
      label: PROFILER_FINANCIAL_STATEMENTS.EBT.name,
      subLabel: 'in % of sales',
      data: ebt,
      kind: 'currency'
    },
    {
      key: 'net-income',
      label: 'Net income',
      subLabel: 'in % of sales',
      data: netIncome,
      kind: 'currency'
    },
    {
      key: 'total-assets',
      label: 'Total assets',
      subLabel: null,
      data: totalAssets,
      kind: 'currency',
      showPercentage: false
    },
    {
      key: 'cash-equivalents',
      label: 'Cash & Equivalents',
      subLabel: null,
      data: cashAndEquivalents,
      kind: 'currency',
      showPercentage: false
    },
    {
      key: 'employees',
      label: 'Employees',
      subLabel: employeesHaveGrowth ? 'Growth in %' : null,
      data: employees,
      kind: 'employees',
      showPercentage: employeesHaveGrowth
    }
  ];
  const visibleMetricRows = showEmptyRows
    ? metricRows
    : metricRows.filter((m) => m.data.length > 0);

  const renderMetricValueRow = (m, isLast) => {
    if (m.kind === 'revenue') {
      return renderRevenueRow(m.data, { isLast });
    }
    if (m.kind === 'employees') {
      return renderEmployeesRow(m.data, { isLast, showPercentage: m.showPercentage === true });
    }
    return renderFinancialRow(m.data, m.key, {
      isLast,
      showPercentage: m.showPercentage !== false
    });
  };

  return (
    <div className={`card financials${showEmptyRows ? '' : ' financials-lite'}`}>
      <div className={`title ${dataSourceClass(FIELD_SOURCE.financials)}`}>Financials</div>
      {isLoading && <GeneralLoader />}
      {!isLoading && hasError && (
        <div className="load-fail-icon">
          <img src={ICON_BUTTON_ICONS.EXCLAMATAION_TRIANGLE_FILL} alt="" />
        </div>
      )}
      {!isLoading && !hasError && (
        <>
          {layout === 'mobile' && isScrollable && (
            <IconButton
              id="company-details-financials-info-scroll-mobile"
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.ARROW_LEFT_RIGHT}
              withBorder={false}
              tooltipClassName="company-details-financials-info-scroll-mobile-tooltip"
              tooltipContent={
                <div>
                  <div className="info-title">Scrollable content</div>
                  <div>
                    <img src="/images/company-details-financials-scrollable-info.gif" alt="" />
                  </div>
                </div>
              }
            />
          )}
          <div className="general-financials-layout">
            {/* Labels Column */}
            <div className="general-financials-labels">
              <div className="labels head-labels">
                <div className="label head-label">
                  <ProfilerCurrencyFormatSwitch />
                </div>
              </div>
              {visibleMetricRows.map((m, i) => (
                <div
                  className={`labels${i === visibleMetricRows.length - 1 ? ' last' : ''}`}
                  key={m.key}
                >
                  <div className="label">{m.label}</div>
                  {m.subLabel && <div className="sub-label">{m.subLabel}</div>}
                </div>
              ))}
            </div>

            {/* Values Columns */}
            <div className="general-financials-values" ref={valuesRef}>
              {/* Year Headers */}
              <div className="labels-wrap head-labels-wrap">
                {years.map((year) => (
                  <div className="labels head-labels" key={year + 'hl'} style={labelStyle}>
                    <div className="label head-label">{year}</div>
                  </div>
                ))}
                {!years.length && (
                  <div className="labels head-labels" style={labelStyle}>
                    <div className="label head-label">{PUNCTUATION.EMPTY_VALUE}</div>
                  </div>
                )}
              </div>

              {visibleMetricRows.map((m, i) => (
                <Fragment key={m.key}>
                  {renderMetricValueRow(m, i === visibleMetricRows.length - 1)}
                </Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

GeneralTabFinancialsCard.propTypes = {
  financials: PropTypes.object,
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
  layout: PropTypes.oneOf(['desktop', 'mobile', 'tablet']),
  isScrollable: PropTypes.bool,
  labelStyle: PropTypes.object,
  formatCurrency: PropTypes.func,
  valuesRef: PropTypes.object,
  canViewExtendedData: PropTypes.bool,
  showEmptyRows: PropTypes.bool
};

export default GeneralTabFinancialsCard;
